/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { PositionReportSource } from 'src/graphql/generated/graphql.generated';
import {
  CoordinatesRecord,
  CoordinatesProps,
} from 'src/store/generated/coordinates-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface PositionReportArchiveProps {
  id: string;
  positionTime: Date;
  source: PositionReportSource;
  dateArchived: Date;
  tripId: string;
  coordinates: CoordinatesProps | null;
  altitude: number | null;
  speedInKnots: number | null;
  heading: number | null;
  estimatedMinutesEnRoute: number | null;
  fuelRemaining: string | null;
  timezoneCd: string | null;
  dateConfirmed: Date | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class PositionReportArchiveRecord extends Record<PositionReportArchiveProps>({
  id: null as any,
  positionTime: null as any,
  source: null as any,
  dateArchived: null as any,
  tripId: null as any,
  coordinates: null as any,
  altitude: null as any,
  speedInKnots: null as any,
  heading: null as any,
  estimatedMinutesEnRoute: null as any,
  fuelRemaining: null as any,
  timezoneCd: null as any,
  dateConfirmed: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    positionTime: string | number | Date;
    source: PositionReportSource;
    dateArchived: string | number | Date;
    tripId: string;
    coordinates: any;
    altitude: number | null;
    speedInKnots: number | null;
    heading: number | null;
    estimatedMinutesEnRoute: number | null;
    fuelRemaining: string | null;
    timezoneCd: string | null;
    dateConfirmed: string | number | Date | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): PositionReportArchiveRecord {
    if (json == null) {
      throw new Error('PositionReportArchiveRecord.fromJS: parameter cannot be null');
    }
    return new PositionReportArchiveRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'PositionReportArchiveJson.id',
      ),
      positionTime: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'positionTime', '0001-01-01T00:00:00.00Z'),
          'PositionReportArchiveJson.positionTime',
        ),
      ),
      source: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'source', PositionReportSource.Pilot),
        'PositionReportArchiveJson.source',
      ),
      dateArchived: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'dateArchived', '0001-01-01T00:00:00.00Z'),
          'PositionReportArchiveJson.dateArchived',
        ),
      ),
      tripId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'tripId', '00000000-0000-0000-0000-000000000000'),
        'PositionReportArchiveJson.tripId',
      ),
      coordinates: json.coordinates != null ? new CoordinatesRecord(json.coordinates) : null,
      altitude: json.altitude,
      speedInKnots: json.speedInKnots,
      heading: json.heading,
      estimatedMinutesEnRoute: json.estimatedMinutesEnRoute,
      fuelRemaining: json.fuelRemaining,
      timezoneCd: json.timezoneCd,
      dateConfirmed: json.dateConfirmed != null ? new Date(json.dateConfirmed) : null,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'PositionReportArchiveJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    positionTime: string | number | Date;
    source: PositionReportSource;
    dateArchived: string | number | Date;
    tripId: string;
    coordinates: any;
    altitude: number | null;
    speedInKnots: number | null;
    heading: number | null;
    estimatedMinutesEnRoute: number | null;
    fuelRemaining: string | null;
    timezoneCd: string | null;
    dateConfirmed: string | number | Date | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [PositionReportArchiveRecord, Error[]] {
    if (json == null) {
      throw new Error('PositionReportArchiveRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new PositionReportArchiveRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'PositionReportArchiveJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      positionTime: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'positionTime', '0001-01-01T00:00:00.00Z'),
              'PositionReportArchiveJson.positionTime',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      source: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'source', PositionReportSource.Pilot),
            'PositionReportArchiveJson.source',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateArchived: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'dateArchived', '0001-01-01T00:00:00.00Z'),
              'PositionReportArchiveJson.dateArchived',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      tripId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'tripId', '00000000-0000-0000-0000-000000000000'),
            'PositionReportArchiveJson.tripId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      coordinates: tryOrDefault(
        () => (json.coordinates != null ? new CoordinatesRecord(json.coordinates) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      altitude: tryOrDefault(
        () => json.altitude,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      speedInKnots: tryOrDefault(
        () => json.speedInKnots,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      heading: tryOrDefault(
        () => json.heading,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      estimatedMinutesEnRoute: tryOrDefault(
        () => json.estimatedMinutesEnRoute,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fuelRemaining: tryOrDefault(
        () => json.fuelRemaining,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      timezoneCd: tryOrDefault(
        () => json.timezoneCd,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateConfirmed: tryOrDefault(
        () => (json.dateConfirmed != null ? new Date(json.dateConfirmed) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'PositionReportArchiveJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: PositionReportArchiveProps) {
    super(props);
    if (props == null) {
      throw new Error('PositionReportArchiveRecord constructor parameter cannot be null');
    }
  }
}

export abstract class PositionReportArchiveViewModelBase implements PositionReportArchiveProps {
  protected readonly _props: PositionReportArchiveProps;
  private _json?: PositionReportArchiveProps;

  get id(): string {
    return this._props.id;
  }
  get positionTime(): Date {
    return this._props.positionTime;
  }
  get source(): PositionReportSource {
    return this._props.source;
  }
  get dateArchived(): Date {
    return this._props.dateArchived;
  }
  get tripId(): string {
    return this._props.tripId;
  }
  get coordinates(): CoordinatesProps | null {
    return this._props.coordinates;
  }
  get altitude(): number | null {
    return this._props.altitude;
  }
  get speedInKnots(): number | null {
    return this._props.speedInKnots;
  }
  get heading(): number | null {
    return this._props.heading;
  }
  get estimatedMinutesEnRoute(): number | null {
    return this._props.estimatedMinutesEnRoute;
  }
  get fuelRemaining(): string | null {
    return this._props.fuelRemaining;
  }
  get timezoneCd(): string | null {
    return this._props.timezoneCd;
  }
  get dateConfirmed(): Date | null {
    return this._props.dateConfirmed;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isGpsAuto() {
    return this._props.source === PositionReportSource.GpsAuto;
  }
  public get isGpsAutoAdsb() {
    return this._props.source === PositionReportSource.GpsAutoAdsb;
  }
  public get isGpsManual() {
    return this._props.source === PositionReportSource.GpsManual;
  }
  public get isPilot() {
    return this._props.source === PositionReportSource.Pilot;
  }
  public get isTCS() {
    return this._props.source === PositionReportSource.TCS;
  }

  public isAnySource(...positionReportSource: PositionReportSource[]) {
    return positionReportSource.includes(this._props.source);
  }

  constructor(props: PositionReportArchiveProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        positionTime: this._props.positionTime,
        source: this._props.source,
        dateArchived: this._props.dateArchived,
        tripId: this._props.tripId,
        coordinates: this._props.coordinates,
        altitude: this._props.altitude,
        speedInKnots: this._props.speedInKnots,
        heading: this._props.heading,
        estimatedMinutesEnRoute: this._props.estimatedMinutesEnRoute,
        fuelRemaining: this._props.fuelRemaining,
        timezoneCd: this._props.timezoneCd,
        dateConfirmed: this._props.dateConfirmed,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllPositionReportArchiveEntities = (state: AppState) =>
  state.entityStores.positionReportArchive.entities;

export const selectPositionReportArchiveById = (id: string) => (state: AppState) =>
  state.entityStores.positionReportArchive.entities.get(id);

export const selectPositionReportArchiveMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.positionReportArchive.meta.get(id);

export const selectPositionReportArchiveAndMetadataById = (id: string) =>
  createSelector(
    selectPositionReportArchiveById(id),
    selectPositionReportArchiveMetadataById(id),
    (positionReportArchive, meta) => ({ positionReportArchive, meta }),
  );

export function selectPositionReportArchiveEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllPositionReportArchiveEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, PositionReportArchiveRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectPositionReportArchiveEntitiesByPositionTime(positionTime: Date) {
  return createSelector(selectAllPositionReportArchiveEntities, (entities) =>
    entities.filter((e) => e.positionTime === positionTime).valueSeq(),
  );
}

export function selectPositionReportArchiveEntitiesBySource(source: PositionReportSource) {
  return createSelector(selectAllPositionReportArchiveEntities, (entities) =>
    entities.filter((e) => e.source === source).valueSeq(),
  );
}

export function selectPositionReportArchiveEntitiesByDateArchived(dateArchived: Date) {
  return createSelector(selectAllPositionReportArchiveEntities, (entities) =>
    entities.filter((e) => e.dateArchived === dateArchived).valueSeq(),
  );
}

export function selectPositionReportArchiveEntitiesByTripId(tripId: string) {
  return createSelector(selectAllPositionReportArchiveEntities, (entities) =>
    entities.filter((e) => e.tripId === tripId).valueSeq(),
  );
}

export function selectPositionReportArchiveEntitiesByAltitude(altitude: number) {
  return createSelector(selectAllPositionReportArchiveEntities, (entities) =>
    entities.filter((e) => e.altitude === altitude).valueSeq(),
  );
}

export function selectPositionReportArchiveEntitiesBySpeedInKnots(speedInKnots: number) {
  return createSelector(selectAllPositionReportArchiveEntities, (entities) =>
    entities.filter((e) => e.speedInKnots === speedInKnots).valueSeq(),
  );
}

export function selectPositionReportArchiveEntitiesByHeading(heading: number) {
  return createSelector(selectAllPositionReportArchiveEntities, (entities) =>
    entities.filter((e) => e.heading === heading).valueSeq(),
  );
}

export function selectPositionReportArchiveEntitiesByEstimatedMinutesEnRoute(
  estimatedMinutesEnRoute: number,
) {
  return createSelector(selectAllPositionReportArchiveEntities, (entities) =>
    entities.filter((e) => e.estimatedMinutesEnRoute === estimatedMinutesEnRoute).valueSeq(),
  );
}

export function selectPositionReportArchiveEntitiesByFuelRemaining(fuelRemaining: string) {
  return createSelector(selectAllPositionReportArchiveEntities, (entities) =>
    entities.filter((e) => e.fuelRemaining === fuelRemaining).valueSeq(),
  );
}

export function selectPositionReportArchiveEntitiesByTimezoneCd(timezoneCd: string) {
  return createSelector(selectAllPositionReportArchiveEntities, (entities) =>
    entities.filter((e) => e.timezoneCd === timezoneCd).valueSeq(),
  );
}

export function selectPositionReportArchiveEntitiesByDateConfirmed(dateConfirmed: Date) {
  return createSelector(selectAllPositionReportArchiveEntities, (entities) =>
    entities.filter((e) => e.dateConfirmed === dateConfirmed).valueSeq(),
  );
}

export function selectPositionReportArchiveEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllPositionReportArchiveEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
